import { Controller } from '@hotwired/stimulus'
import { Modal } from 'bootstrap'

export default class extends Controller {
  static targets = ['dialog']
  static values = { transition: Boolean }

  initialize() {
    this.modal = new Modal(this.element)

    // Bound once so add/removeEventListener target the same reference.
    this.markDirection = this.markDirection.bind(this)
    this.forgetDirection = this.forgetDirection.bind(this)
    this.wrapStreamRender = this.wrapStreamRender.bind(this)

    // A turbo event cannot stand in for this one: every modal link carries data-turbo-stream,
    // and Turbo answers that click by submitting a synthetic form appended to the body, so
    // neither turbo:click nor turbo:submit-start ever sees the element that was clicked.
    // A submit needs no listener of its own while every form is sent by clicking its button.
    // Captured, or the close button would be handled before the click reached this listener:
    // its modal#close hides the sheet, so forgetDirection would run first and the mark it
    // leaves behind would slide the screen underneath on the next open.
    this.element.addEventListener('click', this.markDirection, true)
    this.element.addEventListener('hide.bs.modal', this.forgetDirection)

    // The step swap is a <turbo-stream action="replace" target="modal_dialog">, not a frame
    // render: layout 'modal' renders full documents, and turbo-rails wraps those in a stream
    // when the request carries both Turbo-Frame and data-turbo-stream. Turbo appends that
    // <turbo-stream> to <html> before running it, outside #modal, so this has to listen on
    // document rather than on the frame itself.
    document.addEventListener(
      'turbo:before-stream-render',
      this.wrapStreamRender
    )
  }

  disconnect() {
    clearTimeout(this.pendingClose)
    this.modal.dispose()
    this.element.removeEventListener('click', this.markDirection, true)
    this.element.removeEventListener('hide.bs.modal', this.forgetDirection)
    document.removeEventListener(
      'turbo:before-stream-render',
      this.wrapStreamRender
    )
  }

  dialogTargetConnected() {
    clearTimeout(this.pendingClose)
    this.open()
  }

  // Advancing inside a flow swaps the dialog: the old one leaves and a new one
  // arrives in the same tick. Closing on that swap would fight the reopen, so the
  // decision waits for the DOM to settle and only closes if no dialog is left.
  dialogTargetDisconnected() {
    this.pendingClose = setTimeout(() => {
      if (!this.hasDialogTarget) this.close()
    })
  }

  open() {
    this.modal.show()
  }

  close() {
    this.modal.hide()
  }

  // Anything acted on inside a modal moves the flow forward; only a control that
  // declares itself a step back reverses the slide.
  markDirection(event) {
    const origin = event.target.closest('[data-modal-direction]')

    this.direction = origin?.dataset.modalDirection || 'forward'
  }

  // Closing leaves the click that closed it marked and no render to spend it on, and
  // opening the next modal is a modal_dialog replace like any step — so it would slide.
  forgetDirection() {
    this.direction = null
  }

  wrapStreamRender(event) {
    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches
    // the LP has no slide of its own, so its modals keep swapping steps outright
    if (!this.transitionValue) return
    if (!this.direction || reducedMotion || !document.startViewTransition)
      return
    if (event.target.target !== 'modal_dialog') return

    const defaultRender = event.detail.render
    const direction = this.direction
    this.direction = null

    event.detail.render = (streamElement) => {
      let transition

      document.documentElement.dataset.maDirection = direction

      try {
        transition = document.startViewTransition(() =>
          defaultRender(streamElement)
        )
      } catch {
        // a transition already in flight — fall back to the instant swap
        delete document.documentElement.dataset.maDirection
        return defaultRender(streamElement)
      }

      transition.finished.finally(
        () => delete document.documentElement.dataset.maDirection
      )
      return transition.updateCallbackDone
    }
  }
}
